/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { CheckmarkFilled, WarningFilled, ErrorFilled, UnknownFilled } from '@carbon/icons-react';
import { InlineLoading } from '@carbon/react';

import { StatusWrapper, WarningCount, Tooltip } from './Status.styled';

/**
 * @typedef {'success' | 'warning' | 'error' | 'loading' | 'idle' | 'unknown' } StatusType
 */

/** @type {Record<Exclude<StatusType, 'loading' | 'idle'>, JSX.Element>} */
const STATUS_ICONS = {
  success: <CheckmarkFilled className="success" />,
  warning: <WarningFilled className="warning" />,
  error: <ErrorFilled className="error" />,
  unknown: <UnknownFilled className="unknown" />
};

/**
 * @param {Object} props
 * @param {StatusType} props.status
 * @param {string} props.tooltipMessage
 * @param {number} [props.warningsCount]
 */
export const Status = ({ status, tooltipMessage, warningsCount = 0 }) => {
  if (status === 'idle') {
    return null;
  }

  return (
    <Tooltip align="bottom" label={tooltipMessage}>
      <StatusWrapper role={'status'}>
        {status === 'loading' ? (
          <InlineLoading status="active" description="" />
        ) : (
          <>
            {STATUS_ICONS[status]}
            {warningsCount > 0 && <WarningCount>{warningsCount}</WarningCount>}
          </>
        )}
      </StatusWrapper>
    </Tooltip>
  );
};
